<template>
  <div>
    <Bar
      height="200"
      :chartData="ChartData"
      :chart-options="chartOptions"
      :plugins="chartPlugins" />
  </div>
</template>

<script>
import {
  Chart,
  registerables,
} from 'chart.js';
import { Bar } from 'vue-chartjs';

Chart.register(...registerables);

export default {
  name: 'MixedChart',
  components: {
    Bar,
  },
  props: {
    labelsData: { // 表に付属する色と対応しているデータの情報を表すラベル
      type: Array,
      default: null,
    },
    datasetsData: { // グラフ描画用データ
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      chartPlugins: [ // バーの幅を一定に保つためのプラグイン
        {
          id: 'fixedBarGap',
          beforeDraw(chart) {
            const maxGap = 5; // バー間の最大gap（px）
            const gapRatio = 0.05; // スロット幅の5%をgapに充てる
            const maxThickness = 30; // バーの最大厚さ（px）
            const { chartArea } = chart;
            if (!chartArea) return;

            const numCategories = chart.data.labels.length; // カテゴリー数（x軸の項目数）
            if (numCategories <= 0) return;

            // 表示中のスタック（横並びのバーグループ）を特定
            const stackOrder = [];
            const stackSet = new Set();
            chart.data.datasets.forEach((ds, i) => {
              const meta = chart.getDatasetMeta(i);
              if (meta.type === 'bar' && chart.isDatasetVisible(i)) {
                const stackName = ds.stack || `__default_${i}`;
                if (!stackSet.has(stackName)) {
                  stackSet.add(stackName);
                  stackOrder.push(stackName);
                }
              }
            });

            const numStacks = stackOrder.length; // スタック数（横並びのバーグループ数）
            if (numStacks <= 0) return;

            const availableWidth = chartArea.right - chartArea.left;
            const categoryWidth = availableWidth / numCategories;
            const slotWidth = categoryWidth / numStacks;
            const gap = Math.min(maxGap, slotWidth * gapRatio);

            const barWidth = Math.min(Math.max(1, slotWidth - gap), maxThickness);
            const groupWidth = numStacks * barWidth + (numStacks - 1) * gap;

            // 各バー要素の幅とx座標を再配置
            chart.data.datasets.forEach((ds, i) => {
              const meta = chart.getDatasetMeta(i);
              if (meta.type !== 'bar' || !chart.isDatasetVisible(i)) return;

              const stackName = ds.stack || `__default_${i}`;
              const stackIdx = stackOrder.indexOf(stackName);

              meta.data.forEach((el, catIdx) => {
                const categoryCenter = chartArea.left + categoryWidth * (catIdx + 0.5);
                el.x = categoryCenter - groupWidth / 2 + barWidth / 2 + stackIdx * (barWidth + gap);
                el.width = barWidth;
              });
            });
          },
        },
      ],
      ChartData: {
        labels: [],
        datasets: [],
      },
      chartOptions: {
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true, // pointStyleを使用する設定をここに配置
              // カスタム凡例生成関数
              generateLabels: (chart) => {
                const datasets = chart.data.datasets;
                return datasets.map((dataset, i) => {
                  const isCocomoniCheck = dataset.label === 'ココモニチェック';
                  return {
                    text: dataset.label,
                    fillStyle: dataset.backgroundColor,
                    strokeStyle: dataset.borderColor,
                    lineWidth: dataset.type === 'line' ? 2 : 0,
                    hidden: !chart.isDatasetVisible(i),
                    index: i,
                    datasetIndex: i,
                    // ココモニチェックのみ円形、他は長方形
                    pointStyle: isCocomoniCheck ? 'circle' : 'rect',
                  };
                });
              },
            },
            onClick: (_e, legendItem, legend) => {
              // 凡例クリック時のデフォルト動作（データセットの表示/非表示切り替え）
              const index = legendItem.datasetIndex;
              const chart = legend.chart;
              const meta = chart.getDatasetMeta(index);
              meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
              chart.update();
            },
          },
        },
        datasets: { // 棒グラフ、線グラフをそれぞれ指定して個別にオプションを設定できる
          line: {
            borderWidth: 2,
          },
          bar: {}, // バーの幅はプラグインで制御するため、ここでは特に指定しない
        },
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true,
          },
          'right-y-axis': { // 月毎who5の値は右側のy軸にスケールを表示（ココロの健康診断と同じスケール）
            type: 'linear',
            position: 'right',
            min: -1.1, // 0点の場合にポイントが見づらくなることへの対処
            max: 26.1, // 25点の場合にポイントが見づらくなることへの対処
            ticks: {
              callback: (value) => ([0, 5, 10, 15, 20, 25].includes(value) ? value : null), // y軸目盛の明示指定
            },
            grid: { // 男女の利用件数のグリッドと微妙にズレてるため非表示
              display: false,
            },
          },
        },
      },
    };
  },
  created() {
  },
  mounted() {
    this.drawGraph();
  },
  watch: {
    labelsData() { // 期日や絞り込み条件の変動があったら再描画
      this.updateGraph();
    },
  },
  methods: {
    updateGraph() {
      this.ChartData.labels = [];
      this.ChartData.datasets = [];
      this.drawGraph();
    },
    drawGraph() {
      Object.keys(this.labelsData).forEach((index) => {
        this.ChartData.labels.push(this.labelsData[index]);
      });
      Object.keys(this.datasetsData).forEach((index) => {
        this.ChartData.datasets.push(this.datasetsData[index]);
      });
    },
  },
};
</script>
